import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getInvoicingConnectUrl,
  getInvoicingConnectionStatus,
  getInvoicingRequests,
  toggleInvoicingReadyStatus,
  disconnectInvoicing,
  getInvoicingClient,
  getInvoicingClients,
  getInvoicingCashControlAccount,
  setInvoicingClientLenderFinancing,
  setInvoicingClientDefaultNoteTemplate,
  getInvoicingProducts,
  getInvoicingTerms,
  getInvoicingTaxCodes,
  triggerInvoicingInitialSync,
  triggerInvoicingClientsSync,
  triggerInvoicingProductsSync,
  getInvoicingAdminConnectionInfo,
  refreshInvoicingConnectionProfile,
  getInvoices,
  getInvoiceDetail,
  getPublicInvoice,
  approvePublicInvoice,
  rejectPublicInvoice,
  getInvoiceSettings,
  createInvoice,
  updateInvoice,
  voidInvoice,
  retryInvoiceSync,
  updateInvoiceSettings,
  getInvoiceSummary,
  getInvoiceNoteTemplates,
  createInvoiceNoteTemplate,
  updateInvoiceNoteTemplate,
  deleteInvoiceNoteTemplate,
} from '../services/invoicing';
import type {
  InvoicingConnectionStatus,
  InvoicingInitialSyncStatus,
  InvoicingRequest,
  InvoicingClient,
  InvoicingCashControlAccount,
  InvoicingClientsParams,
  InvoicingProduct,
  InvoicingProductsParams,
  InvoicingTerm,
  InvoicingTermsParams,
  InvoicingTaxCode,
  InvoicingTaxCodesParams,
  InvoicingAdminConnectionInfo,
  Invoice,
  InvoiceDetail,
  PublicInvoice,
  InvoiceSettings,
  InvoiceWritePayload,
  InvoicesFilters,
  InvoiceSummary,
  InvoiceNoteTemplate,
} from '../services/invoicing';
import { PaginatedResponse } from '../services/types';

// Poll while the initial sync is running so the UI flips to the populated table
// as soon as the import finishes, without the user refreshing.
const SYNC_POLL_INTERVAL_MS = 3000;

export const INVOICING_CONNECTION_STATUS_QUERY_KEY = 'invoicing-connection-status';
export const INVOICING_REQUESTS_QUERY_KEY = 'invoicing-requests';
export const INVOICING_CLIENTS_QUERY_KEY = 'invoicing-clients';
export const INVOICING_CASH_CONTROL_ACCOUNT_QUERY_KEY = 'invoicing-cash-control-account';
export const INVOICING_PRODUCTS_QUERY_KEY = 'invoicing-products';
export const INVOICING_TERMS_QUERY_KEY = 'invoicing-terms';
export const INVOICING_TAX_CODES_QUERY_KEY = 'invoicing-tax-codes';

export const INVOICING_STATUS_CONNECTED = 'connected';

// Values the backend appends when redirecting back from the OAuth callback —
// see apps/products/invoicing/views/callback.py.
export const INVOICING_CONNECTED_PARAM_SUCCESS = '1';
export const INVOICING_CONNECTED_PARAM_ERROR = '0';

export type InvoicingEntity = 'clients' | 'products';

const isSyncInFlight = (status: InvoicingInitialSyncStatus | undefined) =>
  status === 'queued' || status === 'running';

export const useInvoicingConnectionStatus = (companyId: number | undefined) => {
  return useQuery<InvoicingConnectionStatus>({
    queryKey: [INVOICING_CONNECTION_STATUS_QUERY_KEY, companyId],
    queryFn: () => getInvoicingConnectionStatus(companyId!),
    enabled: !!companyId,
    // Poll while ANY sync job that could affect this connection is in flight —
    // the combined initial sync, or either entity-scoped manual re-sync.
    refetchInterval: (data) =>
      isSyncInFlight(data?.initialSyncStatus) ||
      isSyncInFlight(data?.clientsSyncStatus) ||
      isSyncInFlight(data?.productsSyncStatus)
        ? SYNC_POLL_INTERVAL_MS
        : false,
  });
};

// Not cached — every click needs a fresh Intuit consent URL (it embeds a short-lived,
// single-use state token), so this is a mutation rather than a query.
export const useInvoicingConnectUrl = () => {
  return useMutation({
    mutationFn: (companyId: number) => getInvoicingConnectUrl(companyId),
  });
};

export const useInvoicingRequests = (companyId: number | undefined) => {
  return useQuery<InvoicingRequest[]>({
    queryKey: [INVOICING_REQUESTS_QUERY_KEY, companyId],
    queryFn: () => getInvoicingRequests(companyId!),
    enabled: !!companyId,
  });
};

export const useToggleInvoicingReadyStatus = () => {
  return useMutation({
    mutationFn: (companyId: number) => toggleInvoicingReadyStatus(companyId),
  });
};

export const useDisconnectInvoicing = () => {
  return useMutation({
    mutationFn: (companyId: number) => disconnectInvoicing(companyId),
  });
};

export const useTriggerInvoicingInitialSync = () => {
  return useMutation({
    mutationFn: (companyId: number) => triggerInvoicingInitialSync(companyId),
  });
};

const ENTITY_SYNC_TRIGGERS = {
  clients: triggerInvoicingClientsSync,
  products: triggerInvoicingProductsSync,
} as const;

export const useTriggerInvoicingEntitySync = (entity: InvoicingEntity) => {
  return useMutation({
    mutationFn: (companyId: number) => ENTITY_SYNC_TRIGGERS[entity](companyId),
  });
};

export const useInvoicingClients = (params: InvoicingClientsParams) => {
  return useQuery<PaginatedResponse<InvoicingClient>>({
    queryKey: [
      INVOICING_CLIENTS_QUERY_KEY,
      params.companyId,
      params.page,
      params.pageSize,
      params.search,
      params.status,
    ],
    queryFn: () => getInvoicingClients(params),
    enabled: !!params.companyId,
    keepPreviousData: true,
  });
};

// One client resolved by id, for the case where the caller knows which client
// it needs but can't guarantee it's inside the paginated list it already
// fetched. `enabled` lets the caller keep this dormant in the common case.
export const useInvoicingClient = (
  { companyId, clientId }: { companyId: number | undefined; clientId: string | undefined },
  options?: { enabled?: boolean }
) =>
  useQuery<InvoicingClient>({
    queryKey: [INVOICING_CLIENTS_QUERY_KEY, companyId, clientId],
    queryFn: () => getInvoicingClient(companyId!, clientId!),
    enabled: !!companyId && !!clientId && (options?.enabled ?? true),
  });

// Whether the company has a Cash Control account at all. Gates the "financed by
// lender" column in the client list — a company-level answer, so one query for
// the whole page rather than one per row.
export const useInvoicingCashControlAccount = (companyId: number | undefined) =>
  useQuery<InvoicingCashControlAccount>({
    queryKey: [INVOICING_CASH_CONTROL_ACCOUNT_QUERY_KEY, companyId],
    queryFn: () => getInvoicingCashControlAccount(companyId!),
    enabled: !!companyId,
  });

export const useSetInvoicingClientLenderFinancing = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, financedByLender }: { clientId: string; financedByLender: boolean }) =>
      setInvoicingClientLenderFinancing(companyId!, clientId, financedByLender),
    onSuccess: () => queryClient.invalidateQueries([INVOICING_CLIENTS_QUERY_KEY]),
  });
};

// Invalidates the clients query, not the note-templates one: the default lives
// on the client payload (`defaultNoteTemplate`), which is what the invoice form
// reads to decide whether the checkbox starts ticked.
export const useSetInvoicingClientDefaultNoteTemplate = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, noteTemplateId }: { clientId: string; noteTemplateId: string | null }) =>
      setInvoicingClientDefaultNoteTemplate(companyId!, clientId, noteTemplateId),
    onSuccess: () => queryClient.invalidateQueries([INVOICING_CLIENTS_QUERY_KEY]),
  });
};

export const useInvoicingProducts = (params: InvoicingProductsParams) => {
  return useQuery<PaginatedResponse<InvoicingProduct>>({
    queryKey: [
      INVOICING_PRODUCTS_QUERY_KEY,
      params.companyId,
      params.page,
      params.pageSize,
      params.search,
      params.status,
    ],
    queryFn: () => getInvoicingProducts(params),
    enabled: !!params.companyId,
    keepPreviousData: true,
  });
};

export const useInvoicingTerms = (params: InvoicingTermsParams) => {
  return useQuery<PaginatedResponse<InvoicingTerm>>({
    queryKey: [
      INVOICING_TERMS_QUERY_KEY,
      params.companyId,
      params.page,
      params.pageSize,
      params.search,
      params.status,
    ],
    queryFn: () => getInvoicingTerms(params),
    enabled: !!params.companyId,
    keepPreviousData: true,
  });
};

export const useInvoicingTaxCodes = (params: InvoicingTaxCodesParams) => {
  return useQuery<PaginatedResponse<InvoicingTaxCode>>({
    queryKey: [
      INVOICING_TAX_CODES_QUERY_KEY,
      params.companyId,
      params.page,
      params.pageSize,
      params.search,
      params.status,
    ],
    queryFn: () => getInvoicingTaxCodes(params),
    enabled: !!params.companyId,
    keepPreviousData: true,
  });
};

const ENTITY_QUERY_KEYS = {
  clients: INVOICING_CLIENTS_QUERY_KEY,
  products: INVOICING_PRODUCTS_QUERY_KEY,
} as const;

// Each entity's manual "Sync" button triggers only its own scoped re-sync
// (POST clients/sync/ or products/sync/), not the combined initial-sync — that
// combined flow is reserved for the connect-time bootstrap in Invoicing/index.tsx.
export const useInvoicingSyncState = (
  companyId: number | undefined,
  entity: InvoicingEntity,
  callbacks?: { onSyncCompleted?: () => void; onSyncFailed?: () => void }
) => {
  const queryClient = useQueryClient();
  const { data: connection } = useInvoicingConnectionStatus(companyId);
  const { mutate, isLoading: isTriggering } = useTriggerInvoicingEntitySync(entity);

  const entitySyncStatus =
    entity === 'clients' ? connection?.clientsSyncStatus : connection?.productsSyncStatus;
  const isSyncing = entitySyncStatus === 'queued' || entitySyncStatus === 'running';
  const isSyncFailed = entitySyncStatus === 'failed';

  // Keep the latest callbacks in a ref so the transition effect doesn't take them as
  // deps — the views pass inline arrows that change identity on every render.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // When polling flips this entity's sync to `completed`, refetch just its own
  // list so it populates without the user refreshing — the other entity's list
  // is untouched since the job is now scoped.
  const prevSyncStatusRef = useRef(entitySyncStatus);
  useEffect(() => {
    const prevSyncStatus = prevSyncStatusRef.current;
    prevSyncStatusRef.current = entitySyncStatus;

    // `prevSyncStatus` is `undefined` only until the connection query resolves for the
    // first time — the idle/no-sync-in-flight state is `null`, a distinct value. Gating
    // on `undefined` specifically (not falsy) is what skips a stale failed/completed
    // status on mount while still catching a fast sync that jumps straight from `null`
    // to `completed`/`failed` without a `running` tick in between.
    if (prevSyncStatus === undefined || prevSyncStatus === entitySyncStatus) return;

    if (entitySyncStatus === 'completed') {
      queryClient.invalidateQueries([ENTITY_QUERY_KEYS[entity], companyId]);
      callbacksRef.current?.onSyncCompleted?.();
      return;
    }

    if (entitySyncStatus === 'failed') callbacksRef.current?.onSyncFailed?.();
  }, [entitySyncStatus, companyId, entity, queryClient]);

  const triggerSync = () => {
    if (!companyId) return;
    mutate(companyId, {
      onSuccess: () => {
        queryClient.invalidateQueries([INVOICING_CONNECTION_STATUS_QUERY_KEY, companyId]);
      },
    });
  };

  return { isSyncing, isSyncFailed, isTriggering, triggerSync };
};

export const INVOICING_ADMIN_CONNECTION_INFO_QUERY_KEY = 'invoicing-admin-connection-info';

export const useInvoicingAdminConnectionInfo = (companyId: number | undefined) => {
  return useQuery<InvoicingAdminConnectionInfo>({
    queryKey: [INVOICING_ADMIN_CONNECTION_INFO_QUERY_KEY, companyId],
    queryFn: () => getInvoicingAdminConnectionInfo(companyId!),
    enabled: !!companyId,
  });
};

export const useRefreshInvoicingConnectionProfile = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => refreshInvoicingConnectionProfile(companyId!),
    onSuccess: () => queryClient.invalidateQueries([INVOICING_ADMIN_CONNECTION_INFO_QUERY_KEY]),
  });
};

export const INVOICES_QUERY_KEY = 'invoices';
export const INVOICE_DETAIL_QUERY_KEY = 'invoice-detail';
export const INVOICE_SETTINGS_QUERY_KEY = 'invoice-settings';

export const useInvoices = (companyId: number | undefined, filters: InvoicesFilters = {}) =>
  useQuery<PaginatedResponse<Invoice>>({
    queryKey: [INVOICES_QUERY_KEY, companyId, filters],
    queryFn: () => getInvoices(companyId!, filters),
    enabled: !!companyId,
  });

export const useInvoiceDetail = (companyId: number | undefined, invoiceId: string | undefined) =>
  useQuery<InvoiceDetail>({
    queryKey: [INVOICE_DETAIL_QUERY_KEY, companyId, invoiceId],
    queryFn: () => getInvoiceDetail(companyId!, invoiceId!),
    enabled: !!companyId && !!invoiceId,
  });

export const useInvoiceSettings = (companyId: number | undefined) =>
  useQuery<InvoiceSettings>({
    queryKey: [INVOICE_SETTINGS_QUERY_KEY, companyId],
    queryFn: () => getInvoiceSettings(companyId!),
    enabled: !!companyId,
  });

export const INVOICE_SUMMARY_QUERY_KEY = 'invoice-summary';

export const useInvoiceSummary = (companyId: number | undefined, startDate: string, endDate: string) =>
  useQuery<InvoiceSummary>({
    queryKey: [INVOICE_SUMMARY_QUERY_KEY, companyId, startDate, endDate],
    queryFn: () => getInvoiceSummary(companyId!, startDate, endDate),
    enabled: !!companyId,
  });

export const PUBLIC_INVOICE_QUERY_KEY = 'public-invoice';

export const usePublicInvoice = (token: string | undefined) =>
  useQuery<PublicInvoice>({
    queryKey: [PUBLIC_INVOICE_QUERY_KEY, token],
    queryFn: () => getPublicInvoice(token!),
    enabled: !!token,
    retry: false,
  });

export const useApprovePublicInvoice = (token: string | undefined) =>
  useMutation({
    mutationFn: () => approvePublicInvoice(token!),
  });

export const useRejectPublicInvoice = (token: string | undefined) =>
  useMutation({
    mutationFn: (note: string) => rejectPublicInvoice(token!, note),
  });

export const useCreateInvoice = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvoiceWritePayload) => createInvoice(companyId!, payload),
    onSuccess: () => queryClient.invalidateQueries([INVOICES_QUERY_KEY]),
  });
};

export const useUpdateInvoice = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: Partial<InvoiceWritePayload> }) =>
      updateInvoice(companyId!, invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([INVOICES_QUERY_KEY]);
      queryClient.invalidateQueries([INVOICE_DETAIL_QUERY_KEY]);
    },
  });
};

export const useVoidInvoice = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => voidInvoice(companyId!, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries([INVOICES_QUERY_KEY]);
      queryClient.invalidateQueries([INVOICE_DETAIL_QUERY_KEY]);
    },
  });
};

export const useRetryInvoiceSync = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => retryInvoiceSync(companyId!, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries([INVOICES_QUERY_KEY]);
      queryClient.invalidateQueries([INVOICE_DETAIL_QUERY_KEY]);
    },
  });
};

export const useUpdateInvoiceSettings = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<InvoiceSettings> | FormData) => updateInvoiceSettings(companyId!, payload),
    onSuccess: () => queryClient.invalidateQueries([INVOICE_SETTINGS_QUERY_KEY]),
  });
};

export const INVOICE_NOTE_TEMPLATES_QUERY_KEY = 'invoice-note-templates';

export const useInvoiceNoteTemplates = (companyId: number | undefined) =>
  useQuery<PaginatedResponse<InvoiceNoteTemplate>>({
    queryKey: [INVOICE_NOTE_TEMPLATES_QUERY_KEY, companyId],
    queryFn: () => getInvoiceNoteTemplates(companyId!),
    enabled: !!companyId,
  });

export const useSaveInvoiceNoteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      templateId,
      name,
      text,
    }: {
      companyId: number;
      templateId: string | null;
      name?: string;
      text: string;
    }) =>
      templateId
        ? updateInvoiceNoteTemplate(companyId, templateId, text)
        : createInvoiceNoteTemplate(companyId, name ?? '', text),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [INVOICE_NOTE_TEMPLATES_QUERY_KEY, variables.companyId],
      });
      queryClient.invalidateQueries([INVOICING_CLIENTS_QUERY_KEY]);
    },
  });
};

export const useDeleteInvoiceNoteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, templateId }: { companyId: number; templateId: string }) =>
      deleteInvoiceNoteTemplate(companyId, templateId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [INVOICE_NOTE_TEMPLATES_QUERY_KEY, variables.companyId],
      });
      queryClient.invalidateQueries([INVOICING_CLIENTS_QUERY_KEY]);
    },
  });
};
